import React, { useEffect, useState } from 'react';
import { ComponentConfig } from '@measured/puck';
import { MarginProps, marginFields } from '../../puck/reusable-props/NewMargin';
import { PaddingProps, paddingFields } from '../../puck/reusable-props/padding';
import { ReduxSelectorProps } from '../../services/constants';
import CustomTooltip from '../../../components/common/tooltip';
import {
  getBorderProps,
  getcurrentDeviceType,
  getNewMarginStyles,
  getPaddingStyles,
} from '../../utils/helper';
import showComponent, {
  showComponentProps,
} from '../../puck/reusable-props/showComponent';
import { QuillTextEditor } from '../../puck/reusable-props/quill-editor';
import Border, { IBorderValueProps } from '../../puck/reusable-props/Border';
import { backgroungFields } from '../../puck/reusable-props/backgroungFields';
import '../text-editor/style.scss';
import { Switch, Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import WithAccordion from '../customComponents/CustomAccordion';
import ColorPicker from '../../puck/reusable-props/ColorPicker';
import { CustomizeProps } from '../../interface/commonInterface';
import TextCustomInput from '../../puck/reusable-props/TextCustomInput';

export type TabContentProps = {
  tabs: { label: CustomizeProps; content: string }[];
  border: IBorderValueProps;
  customActiveTab: {
    activeHighlightColor: string;
    colorIncludeTabHeading: boolean;
  };
  background: {
    color: string;
    isInherit: boolean;
  };
} & showComponentProps &
  MarginProps &
  PaddingProps;

export const TabContent = ({
  useAppSelector,
}: ReduxSelectorProps): ComponentConfig<TabContentProps> => {
  return {
    label: (
      <CustomTooltip title="Absolute">
        <span>Tab Content</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      ...showComponent,
      tabs: {
        type: 'array',
        label: 'Tabs',
        arrayFields: {
          label: {
            type: 'custom',
            render: ({ value, onChange }) => (
              <TextCustomInput
                showDeviceType={false}
                value={value}
                onChange={onChange}
                title={'Button Text'}
              />
            ),
          },
          content: { ...QuillTextEditor('Tab Content').content },
        },
        defaultItemProps: {
          label: {
            weight: {
              mobile: 'font-normal',
              tablet: 'font-normal',
              desktop: 'font-normal',
            },
            deviceType: 'desktop',
            text: 'New Tab',
            textColor: {
              mobile: '#000000',
              tablet: '#000000',
              desktop: '#000000',
            },
            fontSize: {
              mobile: 'text-base',
              tablet: 'text-base',
              desktop: 'text-base',
            },
          },
          content: '<p>Enter content for this tab</p>',
        },
      },
      customActiveTab: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <WithAccordion title="Custom Active Tab">
            <div className="flex justify-between items-center mb-3">
              <p className="text-sm font-semibold">
                Include same color for heading
              </p>
              <div className="flex gap-0.5 items-center">
                <Tooltip
                  title={
                    'Toggling this will either collapse or expand the Extra Terms by default.'
                  }
                  placement="top-start">
                  <InfoIcon fontSize="small" className="h-1 w-1" />
                </Tooltip>
                <Switch
                  checked={value?.colorIncludeTabHeading}
                  onChange={event =>
                    onChange({
                      ...value,
                      colorIncludeTabHeading: event.target.checked,
                    })
                  }
                  inputProps={{ 'aria-label': 'controlled' }}
                />
              </div>
            </div>
            <ColorPicker
              label="Tab Highlight Color"
              tooltip="backgroundColor"
              onChange={color =>
                onChange({
                  ...value,
                  activeHighlightColor: color,
                })
              }
              value={value?.activeHighlightColor}
            />
          </WithAccordion>
        ),
      },
      ...backgroungFields,
      ...marginFields,
      ...paddingFields,
      border: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <Border value={value} onChange={onChange} title="Border" />
        ),
      },
    },
    defaultProps: {
      showComponent: ['desktop', 'tablet', 'mobile'],
      tabs: [
        {
          label: {
            weight: {
              mobile: 'font-normal',
              tablet: 'font-normal',
              desktop: 'font-normal',
            },
            deviceType: 'desktop',
            text: 'Description',
            textColor: {
              mobile: '#000000',
              tablet: '#000000',
              desktop: '#000000',
            },
            fontSize: {
              mobile: 'text-base',
              tablet: 'text-base',
              desktop: 'text-base',
            },
          },
          content: `
            <p>
              Major brands in our industry markup their products 8-10x the actual cost. We do things differently. 
              To bring luxury within reach, we've built relationships with some of the world's top jewelers 
              from Los New York to Italy to sell directly to you!
            </p>
          `,
        },
        {
          label: {
            weight: {
              mobile: 'font-normal',
              tablet: 'font-normal',
              desktop: 'font-normal',
            },
            deviceType: 'desktop',
            text: 'What’s Inside',
            textColor: {
              mobile: '#000000',
              tablet: '#000000',
              desktop: '#000000',
            },
            fontSize: {
              mobile: 'text-base',
              tablet: 'text-base',
              desktop: 'text-base',
            },
          },
          content: `
            <div>
              <p>
                Unlock excitement with each mystery box containing an assortment of premium jewelry items. 
                Here's what you might find inside:
              </p>
              <ul style="margin-top: 8px; margin-bottom: 8px;">
                <li>Stunning chains and necklaces</li>
                <li>Iced-out watches to elevate your style</li>
                <li>Elegant rings and bracelets</li>
                <li>Exclusive $500 Cartice gift cards</li>
                <li>And many more luxurious surprises!</li>
              </ul>
              <p>
                Treat yourself or a loved one to the ultimate jewelry experience today.
              </p>
            </div>
          `,
        },
      ],
      customActiveTab: {
        colorIncludeTabHeading: false,
        activeHighlightColor: '#000000',
      },
      border: {
        deviceType: 'desktop',
        borderBottomLeft: { desktop: '0', tablet: '0', mobile: '0' },
        borderBottomRight: { desktop: '0', tablet: '0', mobile: '0' },
        borderTopLeft: { desktop: '0', tablet: '0', mobile: '0' },
        borderTopRight: { desktop: '0', tablet: '0', mobile: '0' },
        borderWidth: { desktop: 0, tablet: 0, mobile: 0 },
        borderStyle: {
          desktop: 'border-solid',
          tablet: 'border-solid',
          mobile: 'border-solid',
        },
        borderColor: {
          desktop: '#000000',
          tablet: '#000000',
          mobile: '#000000',
        },
      },
      margin: {
        deviceType: 'desktop',
        marginBottom: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        marginLeft: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        marginRight: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        marginTop: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
      },
      padding: {
        deviceType: 'desktop',
        paddingBottom: { desktop: '0', mobile: '0', tablet: '0' },
        paddingLeft: { desktop: '0', mobile: '0', tablet: '0' },
        paddingRight: { desktop: '0', mobile: '0', tablet: '0' },
        paddingTop: { desktop: '0', mobile: '0', tablet: '0' },
      },
      background: {
        color: '#ffffff',
        isInherit: false,
      },
    },
    render: ({
      tabs,
      margin,
      padding,
      showComponent,
      border,
      background,
      editMode,
      customActiveTab,
    }) => {
      const [activeTab, setActiveTab] = useState(tabs[0]?.label.text || '');
      const windowWidth = useAppSelector(state => state.store.currentWidth);
      const currentDeviceType = getcurrentDeviceType(windowWidth, editMode);

      const marginStyles = getNewMarginStyles(margin, currentDeviceType);
      const paddingStyles = getPaddingStyles(padding, currentDeviceType);
      useEffect(() => {
        setActiveTab(tabs[0]?.label.text);
      }, [tabs[0]?.label.text]);
      return (
        <>
          {showComponent?.includes(currentDeviceType) && (
            <div
              style={{
                ...marginStyles,
                ...paddingStyles,
                ...getBorderProps(border, currentDeviceType),
                backgroundColor: background?.isInherit
                  ? 'inherit'
                  : background?.color,
              }}
              className={`text-editor-content ${border?.borderStyle?.[currentDeviceType]}`}>
              <div className="flex gap-4">
                {tabs.map((tab, index) => (
                  <button
                    key={index}
                    type="button"
                    style={{
                      color:
                        activeTab === tab.label.text &&
                        customActiveTab?.colorIncludeTabHeading
                          ? customActiveTab?.activeHighlightColor
                          : tab?.label?.textColor?.desktop || '#000000',
                      borderColor:
                        activeTab === tab.label.text
                          ? customActiveTab?.activeHighlightColor
                          : 'transparent',
                    }}
                    className={`w-full px-4 py-2 border-b-2 ${tab.label.fontSize?.desktop} ${tab.label.weight?.desktop} ${
                      activeTab === tab.label.text ? ' font-semibold' : ''
                    }`}
                    onClick={() => setActiveTab(tab.label.text)}>
                    {tab.label.text}
                  </button>
                ))}
              </div>
              <div className={`mt-4 `} style={{ lineHeight: '1.5' }}>
                {tabs.map(
                  (tab, index) =>
                    activeTab === tab.label.text && (
                      <div
                        key={index}
                        dangerouslySetInnerHTML={{ __html: tab.content }}
                      />
                    ),
                )}
              </div>
            </div>
          )}
        </>
      );
    },
  };
};
